// Data Deletion Status page.
// Meta receives the URL to this page in the JSON response from our
// /api/meta/data-deletion callback (Phase 3). When a user follows that
// URL we render the current status of their request by polling
// GET /api/meta/data-deletion/:code.

import { useEffect, useState } from 'react';
import Seo from '../components/Seo';
import { API_BASE_URL as API_BASE } from '../lib/api-url';

interface StatusResponse {
  confirmation_code: string;
  status: 'received' | 'processing' | 'completed' | 'failed';
  source: 'data_deletion' | 'deauthorize';
  contacts_affected: number;
  channels_invalidated: number;
  created_at: string;
  completed_at: string | null;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'missing_code' }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string }
  | { kind: 'ok'; data: StatusResponse };

export default function DataDeletionStatus() {
  const code = new URLSearchParams(window.location.search).get('code') || '';
  const [state, setState] = useState<LoadState>(
    code ? { kind: 'loading' } : { kind: 'missing_code' }
  );

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function load() {
      try {
        const res = await fetch(
          `${API_BASE}/api/meta/data-deletion/${encodeURIComponent(code)}`,
          { headers: { Accept: 'application/json' } }
        );
        if (cancelled) return;
        if (res.status === 404) {
          setState({ kind: 'not_found' });
          return;
        }
        if (!res.ok) {
          setState({ kind: 'error', message: `HTTP ${res.status}` });
          return;
        }
        const data = (await res.json()) as StatusResponse;
        setState({ kind: 'ok', data });

        // Auto-refresh while still in flight. The whole job typically
        // finishes in seconds, so we poll twice with a short delay then
        // stop.
        if (data.status === 'received' || data.status === 'processing') {
          timer = setTimeout(load, 3000);
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: err instanceof Error ? err.message : 'Network error',
          });
        }
      }
    }

    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [code]);

  return (
    <section className="bg-ink text-white">
      <Seo
        title="Deletion Request Status · WFS Chatbot"
        description="Check the status of your WFS Chatbot data deletion request."
        path="/data-deletion-status"
      />
      <div className="mx-auto max-w-prose px-6 py-20 md:py-28">
        <span className="rr-tag">// privacy · request status</span>
        <h1 className="mt-4 text-display-md font-bold tracking-headline text-white">
          Deletion request status
        </h1>

        <div className="mt-10">{renderState(state, code)}</div>

        <div className="mt-16 h-px bg-ink-3" aria-hidden />
        <p className="mt-6 text-sm text-ash">
          Need help? Email{' '}
          <a className="text-signal underline" href="mailto:info@wavefrontstudiollc.com">
            info@wavefrontstudiollc.com
          </a>
        </p>
      </div>
    </section>
  );
}

function renderState(state: LoadState, code: string) {
  switch (state.kind) {
    case 'missing_code':
      return (
        <Card heading="No request code">
          <p>
            This page expects a <code className="font-mono text-signal">?code=...</code>{' '}
            parameter in the URL. The full link should have been provided to
            you by Meta after you submitted a data deletion request.
          </p>
        </Card>
      );
    case 'loading':
      return (
        <Card heading="Looking up your request">
          <p className="font-mono text-mono-xs uppercase tracking-mono text-graphite">
            code: {code}
          </p>
          <p className="mt-3">One moment.</p>
        </Card>
      );
    case 'not_found':
      return (
        <Card heading="Request not found">
          <p>
            We could not find a request with code{' '}
            <code className="font-mono text-signal">{code}</code>. Double-check
            the link you were given. If it is correct and this keeps happening,
            email us and we will look it up by hand.
          </p>
        </Card>
      );
    case 'error':
      return (
        <Card heading="Could not load status">
          <p>
            Something went wrong on our side. Please refresh the page in a
            moment, or contact us if the problem persists.
          </p>
          <p className="mt-3 font-mono text-mono-xs uppercase tracking-mono text-graphite">
            error: {state.message}
          </p>
        </Card>
      );
    case 'ok':
      return <StatusDetails data={state.data} />;
  }
}

function StatusDetails({ data }: { data: StatusResponse }) {
  const { status, source, contacts_affected, channels_invalidated, created_at, completed_at } =
    data;

  const heading = (() => {
    if (status === 'completed' && source === 'data_deletion') return 'Your data has been deleted';
    if (status === 'completed' && source === 'deauthorize') return 'Your app access was removed';
    if (status === 'failed') return 'Request failed';
    return 'Request in progress';
  })();

  const summary = (() => {
    if (status === 'received')
      return 'We received your request and queued it for processing.';
    if (status === 'processing')
      return 'We are working on your request right now. This page will refresh on its own.';
    if (status === 'completed' && source === 'data_deletion')
      return contacts_affected > 0
        ? `We anonymized ${contacts_affected} contact record${contacts_affected === 1 ? '' : 's'} tied to your Meta account.`
        : 'No matching contact records were found, so there was nothing to delete.';
    if (status === 'completed' && source === 'deauthorize')
      return channels_invalidated > 0
        ? `We invalidated ${channels_invalidated} channel connection${channels_invalidated === 1 ? '' : 's'} that was installed by you.`
        : 'No matching channel installations were found.';
    if (status === 'failed')
      return 'We hit an error while processing your request. Please email us so we can finish it manually.';
    return '';
  })();

  return (
    <Card heading={heading}>
      <p>{summary}</p>
      <dl className="mt-6 space-y-2 font-mono text-mono-xs uppercase tracking-mono text-graphite">
        <Row label="status" value={status} />
        <Row label="type" value={source === 'data_deletion' ? 'data deletion' : 'app removal'} />
        <Row label="received" value={formatDate(created_at)} />
        {completed_at && <Row label="completed" value={formatDate(completed_at)} />}
        <Row label="code" value={data.confirmation_code} />
      </dl>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3">
      <dt className="w-24 shrink-0 text-graphite">{label}</dt>
      <dd className="text-ash">{value}</dd>
    </div>
  );
}

function Card({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <div className="rounded-card border border-ink-3 bg-ink-2 p-6">
      <h2 className="text-lg font-semibold tracking-headline text-white">{heading}</h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-ash">{children}</div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}
